import { Injectable } from '@angular/core';
import { ProcessPageTypeCodeEnum } from '@interfaces';
import { ISubmitResponse } from '@interfaces/models/ISubmitResponse';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { selectITOAFromStore } from '@shared/services/open-account/oa.selectors';
import { OpenAccountReviewService } from '@shared/services/open-account/review/oa-review.service';
import { OpenAccountSubmitService } from '@shared/services/open-account/submit/oa-submit.service';
import { TealeafTrackingService } from '@shared/services/tealeaf-tracking/tealeaf-tracking.service';
import {
  submitInternalTransferOpenAccount,
  submitInternalTransferOpenAccountComplete,
  submitInternalTransferOpenAccountFailure,
  submitInternalTransferOpenAccountResponse,
  validateInternalTransferOpenAccount,
  validateInternalTransferOpenAccountFailure,
  validateInternalTransferOpenAccountResponse,
} from '@store/actions/open-account/internal-transfer-open-account.actions';
import { catchError, forkJoin, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { selectRecaptchaMockResult } from 'vg-interaction-model';

import { FMFService } from '../../../../shared/services/fraud-micro-frontend/fmf.service';
import { LogError, TrackProcessError } from '../../actions';
import * as routerActions from '../../actions/router/router.actions';
import { openSpinner } from '../../actions/spinner/spinner.actions';
import { internalTransferAppianSubmit } from '../../actions/submit/submit.actions';
import {
  submitMetricsOAEnd,
  submitMetricsOASuccess,
} from '../../actions/submit-metrics/submit-metrics.actions';
import * as fromRootStore from '../../index';
import { selectFraudDetectorSimulatorLevel } from '../../selectors/fraud-detector-simulator/fraud-detector-simulator.selectors';

@Injectable()
export class InternalTransferOpenAccountEffects {
  CONFIRM = 'confirm';
  SYSTEM_ERROR = 'error';

  constructor(
    private actions$: Actions,
    private openAccountReviewService: OpenAccountReviewService,
    private openAccountSubmitService: OpenAccountSubmitService,
    private tealeafTrackingService: TealeafTrackingService,
    private fmfService: FMFService,
    private store$: Store<fromRootStore.State>,
  ) {}

  validateInternalTransferOpenAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validateInternalTransferOpenAccount),
      withLatestFrom(
        this.store$.pipe(select(selectITOAFromStore)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
      ),
      switchMap(([, openAccountData, mockRecaptchaLevel]) => {
        this.tealeafTrackingService
          .callTealeafTracking({
            countryOfResidence: openAccountData?.personalInfo[0]?.countryOfResidence,
            Mailing_Street: openAccountData?.contactInfo[0]?.mailingAddress?.street,
            Mailing_City: openAccountData?.contactInfo[0]?.mailingAddress?.city,
            Mailing_State: openAccountData?.contactInfo[0]?.mailingAddress?.state,
            Mailing_zipCode: openAccountData?.contactInfo[0]?.mailingAddress?.zipCode,
          })
          .subscribe();

        return forkJoin([
          of(openAccountData),
          this.fmfService.getAllTokens({ recaptcha: 'COO_OA_VALIDATE' }),
          of(mockRecaptchaLevel),
        ]);
      }),
      switchMap(([openAccountData, fmfResponse, mockRecaptchaLevel]) => {
        return this.openAccountReviewService
          .openAccountValidate(openAccountData, fmfResponse, mockRecaptchaLevel)
          .pipe(
            switchMap((validated: boolean) => {
              const result = validated
                ? [routerActions.Next()]
                : [
                    routerActions.Go({ path: ['error'] }),
                    LogError({
                      payload: {
                        eventId: 'error-page-route-OA-internal-transfer-validate-fail',
                        message: 'Unable to validate client input',
                        code: LoggerCodes.NULL_FAILURE,
                      },
                    }),
                  ];

              return [
                validateInternalTransferOpenAccountResponse({ payload: validated }),
                ...result,
              ];
            }),
            catchError(() => {
              return [
                validateInternalTransferOpenAccountFailure(),
                submitMetricsOAEnd({
                  payload: ProcessPageTypeCodeEnum.SourceOfFunds,
                }),
                routerActions.Go({ path: ['error'] }),
                LogError({
                  payload: {
                    eventId: 'error-page-route-OA-internal-transfer-validate-error',
                    message: 'Error validating client input',
                    code: LoggerCodes.NULL_FAILURE,
                  },
                }),
              ];
            }),
          );
      }),
    ),
  );

  submitInternalTransferOpenAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitInternalTransferOpenAccount),
      withLatestFrom(
        this.store$.pipe(select(selectITOAFromStore)),
        this.store$.pipe(select(selectRecaptchaMockResult)),
        this.store$.pipe(select(selectFraudDetectorSimulatorLevel)),
      ),
      tap(() => this.store$.dispatch(openSpinner())),
      switchMap(([, openAccountData, mockRecaptchaLevel, mockFraudDetectorLevel]) => {
        return forkJoin([
          of(openAccountData),
          this.fmfService.getAllTokens({ recaptcha: 'COO_OA_SUBMIT' }),
          of(mockFraudDetectorLevel),
          of(mockRecaptchaLevel),
        ]);
      }),
      switchMap(([openAccountData, fmfResponse, mockFraudDetectorLevel, mockRecaptchaLevel]) => {
        const openAccountPayload = { ...openAccountData };
        return this.openAccountSubmitService
          .openAccount(openAccountPayload, fmfResponse, mockFraudDetectorLevel, mockRecaptchaLevel)
          .pipe(
            switchMap((submitResponse: ISubmitResponse) => {
              const actions = [];
              actions.push(
                submitInternalTransferOpenAccountResponse({ payload: submitResponse }),
                submitInternalTransferOpenAccountComplete(),
              );
              if (!submitResponse.success) {
                actions.push(
                  submitInternalTransferOpenAccountFailure(),
                  submitMetricsOAEnd({
                    payload: ProcessPageTypeCodeEnum.Esign,
                  }),
                );
              } else {
                actions.push(submitMetricsOASuccess());
              }
              return actions;
            }),
            catchError((e) => {
              if (e.eventMessage) {
                return [routerActions.Go({ path: ['error'] })];
              }
              return [
                submitInternalTransferOpenAccountResponse({
                  payload: { success: false } as ISubmitResponse,
                }),
                submitInternalTransferOpenAccountComplete(),
                submitInternalTransferOpenAccountFailure(),
                submitMetricsOAEnd({ payload: ProcessPageTypeCodeEnum.Esign }),
              ];
            }),
          );
      }),
    ),
  );

  internalTransferSubmitOpenAccountError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitInternalTransferOpenAccountFailure),
      map(() => {
        return TrackProcessError({
          errorCode: 'coo-oa_error',
        });
      }),
    ),
  );

  internalTransferAppianSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitInternalTransferOpenAccountComplete),
      map(() => {
        return internalTransferAppianSubmit();
      }),
    ),
  );
}
